import {
  Row,
  Col,
  InputGroup,
  Input,
  Button,
  InputGroupText,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import React from "react";
import { connect } from "react-redux";
import { firebaseConnect } from "react-redux-firebase";
import get from "lodash.get";
import classNames from "classnames";
import ReactTooltip from "react-tooltip";
import type { ISortOption } from "../../components/sortDropdown";
import Dashboard from "../../components/dashboard";
import withStyles from "isomorphic-style-loader/withStyles";
import { ExportType } from "../exportModal";
import s from "./styles.scss";
import ISortByType from "../../../types/services/ISortByType";
import {
  executeForgotPassword,
  paginateUsers,
  searchUsers,
} from "../../../utils/fetch";
import ISelectionUpdate from "../../../types/ISelectionUpdate";
import { isCurrentUserAdmin } from "../../../utils/roleCheck";
import {
  serviceCaller,
  withModals,
  ServiceCallerProps,
  ModalProps,
  withLoginRedirect,
} from "../../composition";
import IExtendedUser from "../../../types/services/IExtendedUser";
import IColumnName from "../../../types/IColumnName";
import IThankYouAdminEmail from "../../../types/services/IThankYouAdminEmail";
import ISegment from "../../../types/services/ISegment";
import {
  applyColumnPrefs,
  TableIds,
  toggleSavedColumn,
} from "../../../utils/tablePrefs";

import {
  hideColumnWithoutGiveSpark,
  selectUsesGiveSpark,
} from "../../../utils/giveSpark";
interface Props extends ServiceCallerProps, ModalProps {
  usesGiveSpark: boolean;
  thankYouAdminEmails: { [userId: string]: IThankYouAdminEmail };
  thankYouAdminSegments: {
    [segmentId: string]: { [userId: string]: IThankYouAdminEmail };
  };
  segments: { [segmentId: string]: ISegment };
}

interface State {
  sortBy: ISortByType;
  searchText: string;
  searchTextUsed: string;
  selectAll: boolean;
  actionsMenuOpen: boolean;
  sortAsc: boolean;
  showExportModal: boolean;
  isLoaded: boolean;
  selectedIds: string[];
  suppressedIds: string[];
  totalResult: number;
  columnNames: Array<IColumnName>;
}

const sortOptions: ISortOption[] = [
  { key: "NAME", show: "Name", colName: "name" },
  { key: "ROLE", show: "Role", colName: "rolePretty" },
  { key: "CREATED", show: "Created On", colName: "createdOnPretty" },
];

class UsersPage extends React.PureComponent<Props, State> {
  state = {
    sortBy: sortOptions[0],
    searchText: "",
    searchTextUsed: "",
    selectAll: false,
    actionsMenuOpen: false,
    sortAsc: true,
    showExportModal: false,
    isLoaded: false,
    selectedIds: [],
    suppressedIds: [],
    totalResult: 0,
    columnNames: applyColumnPrefs(TableIds.users, [
      { key: "Name", isDisabled: false },
      { key: "Email", isDisabled: false },
      { key: "Mobile #", isDisabled: false },
      { key: "Role", isDisabled: false },
      { key: "Created On", isDisabled: false },
      { key: "Last Login", isDisabled: false },
      { key: "TY Segments", isDisabled: false },
    ]),
  };

  columnNames = [
    "Name",
    "Email",
    "Mobile #",
    "Role",
    "Created On",
    "Last Login",
    "TY Segments",
  ];

  searchTimeout = null;

  componentDidMount() {
    if (window) {
      this.handleWindowSize();
      window.addEventListener("resize", this.handleWindowSize);
    }
  }

  componentWillUnmount() {
    if (window) {
      window.removeEventListener("resize", this.handleWindowSize);
    }
    if (this.searchTimeout && window) {
      window.clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }

  handleWindowSize = () => {
    if (window) {
      if (window.innerWidth < 768) {
        this.setState({
          columnNames: applyColumnPrefs(TableIds.users, [
            { key: "Name", isDisabled: true },
            { key: "Email", isDisabled: false },
            { key: "Mobile #", isDisabled: true },
            { key: "Role", isDisabled: false },
            { key: "Created On", isDisabled: true },
            { key: "Last Login", isDisabled: true },
            { key: "TY Segments", isDisabled: true },
          ]),
        });
      } else {
        this.setState({
          columnNames: applyColumnPrefs(TableIds.users, [
            { key: "Name", isDisabled: false },
            { key: "Email", isDisabled: false },
            { key: "Mobile #", isDisabled: false },
            { key: "Role", isDisabled: false },
            { key: "Created On", isDisabled: false },
            { key: "Last Login", isDisabled: false },
            { key: "TY Segments", isDisabled: false },
          ]),
        });
      }
    }
  };

  handleColumnHeaderSort = (accessor: string) => {
    const option = sortOptions.find(o => o.colName === accessor);
    if (!option) {
      return;
    }
    this.setState(prev => {
      const same = prev.sortBy.colName === option.colName;
      return {
        sortBy: option,
        sortAsc: same ? !prev.sortAsc : false,
      };
    });
  };

  getResponse = (currentPage, pageSize) => {
    const request = !this.state.searchTextUsed
      ? paginateUsers(
          this.props.config.searchServiceUrl,
          this.props.currentCharity.id,
          currentPage,
          pageSize,
          this.state.sortBy.key,
          this.state.sortAsc,
          this.props.serviceAccessToken
        )
      : searchUsers(
          this.props.config.searchServiceUrl,
          this.props.currentCharity.id,
          this.state.searchTextUsed,
          this.props.serviceAccessToken,
          this.state.sortBy.key,
          this.state.sortAsc
        ).then(response => {
          return {
            result: response,
            time: Date.now(),
            currentPage: 0,
            pageAmount: 1,
            totalAmount: response.length,
          };
        });
    return request.then(this.attachThankYouAssignments);
  };

  attachThankYouAssignments = response => {
    response.result = (response.result || []).map(user => {
      const segmentNames = Object.keys(this.props.thankYouAdminSegments || {})
        .filter(segmentId => {
          const segment = this.props.segments && this.props.segments[segmentId];
          return Boolean(
            segment &&
              segment.isDonorList &&
              !segment.isKeyword &&
              !segment.isAll &&
              this.props.thankYouAdminSegments[segmentId] &&
              this.props.thankYouAdminSegments[segmentId][user.id]
          );
        })
        .map(segmentId => this.props.segments[segmentId].name)
        .sort((a, b) => a.localeCompare(b));
      return {
        ...user,
        thankYouAssignments: {
          isGlobal: Boolean(
            this.props.thankYouAdminEmails &&
              this.props.thankYouAdminEmails[user.id]
          ),
          segmentNames,
        },
      };
    });
    return response;
  };

  assignmentDataKey = () =>
    JSON.stringify([
      this.props.thankYouAdminEmails || {},
      this.props.thankYouAdminSegments || {},
      this.props.segments || {},
    ]);

  selectAllCheck = e => {
    this.setState({
      selectAll: e.target.checked,
      selectedIds: [],
      suppressedIds: [],
    });
  };

  searchTextChange = e => {
    const newText: string = e.target.value;
    if (!newText || newText.length >= 3 || newText.length == 0) {
      if (this.searchTimeout && window) {
        window.clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
      this.setState({
        searchText: newText,
        searchTextUsed: newText,
        selectAll: false,
      });
    } else {
      if (window) {
        if (this.searchTimeout) {
          window.clearTimeout(this.searchTimeout);
        }
        this.setState({
          searchText: newText,
        });
        this.searchTimeout = window.setTimeout(() => {
          this.setState({
            searchTextUsed: newText,
            selectAll: false,
          });
        }, 3000);
      }
    }
  };

  toggleActionsMenu = () => {
    this.setState({ actionsMenuOpen: !this.state.actionsMenuOpen });
  };

  showExportModal(
    exportType: ExportType,
    exportIdsArray?: [string],
    exportTotalIds?: string,
    exportFromDate?: number,
    exportToDate?: number,
    ignoreSelected?: boolean
  ) {
    this.props.toggleExportModal(
      exportType,
      exportIdsArray,
      exportTotalIds,
      exportFromDate,
      exportToDate,
      ignoreSelected
    );
  }

  async getIdsToExport() {
    let totalIds = null;
    if (this.state.selectAll) {
      if (this.state.searchTextUsed) {
        let res = await searchUsers(
          this.props.config.searchServiceUrl,
          this.props.currentCharity.id,
          this.state.searchTextUsed,
          this.props.serviceAccessToken,
          this.state.sortBy.key,
          this.state.sortAsc
        );
        totalIds = res.map(user => user.id);
        if (this.state.suppressedIds.length > 0) {
          totalIds = totalIds.filter(
            id => !this.state.suppressedIds.includes(id)
          );
        }
        return totalIds;
      } else {
        return this.state.suppressedIds;
      }
    }
    return this.state.selectedIds;
  }

  execExport = async () => {
    const exportAmount = this.state.selectAll
      ? this.state.totalResult - this.state.suppressedIds.length
      : this.state.selectedIds.length === 0
      ? this.state.totalResult
      : this.state.selectedIds.length;
    let idsToExport = await this.getIdsToExport();
    this.props.toggleExportModal(
      ExportType.users,
      idsToExport,
      exportAmount.toString(),
      null,
      null,
      this.state.selectAll && !this.state.searchTextUsed
    );
  };

  newUser = () => {
    this.props.navigate("/users/new");
  };

  showUserDetail = (user: IExtendedUser) => {
    this.props.navigate(`/users/${user.id}`);
  };

  resetPassword = (user: IExtendedUser) => {
    executeForgotPassword(this.props.config.authServiceUrl, {
      email: user.email,
    }).then(response => {
      alert(response);
    });
  };

  selectionUpdate = (update: ISelectionUpdate) => {
    this.setState({
      totalResult: update.totalAmount,
      selectedIds: update.selectedIds,
      suppressedIds: update.suppressedIds,
    });
  };

  shouldComponentUpdate(_, nextState: State) {
    return (
      (this.state.selectedIds === nextState.selectedIds &&
        this.state.suppressedIds === nextState.suppressedIds) ||
      this.state.selectAll !== nextState.selectAll
    );
  }

  disableColumn = index => {
    const copy = toggleSavedColumn(
      TableIds.users,
      this.state.columnNames,
      index
    );
    this.setState(
      {
        columnNames: copy,
      },
      () => {
        this.props.columnsModalChange(this.state.columnNames);
      }
    );
  };

  changeColumns = () => {
    this.props.toggleColumnsModal(this.disableColumn, this.state.columnNames);
  };

  columnData = [
    "name",
    "email",
    "phoneNumber",
    "rolePretty",
    "createdOnPretty",
    "lastLoginPretty",
    "thankYouAssignments",
  ];

  extraColumns = [
    {
      id: "users",
      hasTooltip: true,
      tooltipText: "Users Details",
      type: "icon",
      width: 40,
      faIcon: "fa-user-circle",
      className: "customColUsersIcon",
      showFunctions: this.showUserDetail,
    },
    {
      id: "reset",
      hasTooltip: true,
      tooltipText: "Reset Password",
      type: "icon",
      width: 40,
      faIcon: "fa-envelope",
      className: "customColUsersIcon",
      showFunctions: this.resetPassword,
    },
  ];

  render() {
    return (
      <main className={s.main}>
        <div className={s.container}>
          <Row className={s.marginBot}>
            <Col xs={12} className={classNames(s.colMargin, s.noPadding)}>
              <div className={s.usersToolbarFlex}>
                <div className={s.toolbarLeft}>
                  <div
                    className={s.inlineDots}
                    onClick={this.changeColumns}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        this.changeColumns();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    data-tip
                    data-for="users-column-mgmt"
                    aria-label="Column management"
                  >
                    <i className={"fa fa-columns " + s.dotIcon}>&nbsp;</i>
                  </div>
                  <ReactTooltip
                    id="users-column-mgmt"
                    effect="solid"
                    place="top"
                  >
                    Column management
                  </ReactTooltip>
                </div>
                <div className={s.toolbarSearchGrow}>
                  <InputGroup className={s.searchInputFullWidth}>
                    <InputGroupText className={s.inputGroup}>
                      <i className={classNames("fa fa-search", s.searchIcon)}>
                        &nbsp;
                      </i>
                    </InputGroupText>
                    <Input
                      id="searchText"
                      placeholder={"Search"}
                      value={this.state.searchText}
                      onChange={this.searchTextChange}
                      className={s.text}
                    />
                    <InputGroupText className={s.inputGroupRight}>
                      <i
                        className={classNames("fa fa-times", s.closeIcon)}
                        onClick={() =>
                          this.setState({ searchText: "", searchTextUsed: "" })
                        }
                      >
                        &nbsp;
                      </i>
                    </InputGroupText>
                  </InputGroup>
                </div>
                <div className={s.toolbarActions}>
                  {isCurrentUserAdmin(this.props.currentCharity) && (
                    <React.Fragment>
                      <Button
                        id="assignThankYouUsersButton"
                        outline
                        color="primary"
                        className={s.newSecondaryButton}
                        disabled={!this.props.usesGiveSpark}
                        onClick={() =>
                          this.props.navigate("/users/thank-you-admins")
                        }
                      >
                        Assign &quot;Thank You&quot; Users
                      </Button>
                      <Button
                        id="NewUserButton"
                        color="primary"
                        className={s.newPrimaryButton}
                        onClick={this.newUser}
                      >
                        New User
                      </Button>
                    </React.Fragment>
                  )}
                  <Dropdown
                    isOpen={this.state.actionsMenuOpen}
                    toggle={this.toggleActionsMenu}
                  >
                    <DropdownToggle
                      color="link"
                      className={s.toolbarExtras}
                      aria-label="More actions"
                    >
                      <i className="fa fa-ellipsis-v" />
                    </DropdownToggle>
                    <DropdownMenu right className={s.actionsDropdownMenu}>
                      <DropdownItem
                        id="exportUsersButton"
                        onClick={this.execExport}
                      >
                        Export
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            {this.props.serviceAccessToken && this.props.config && (
              <Dashboard
                key={this.assignmentDataKey()}
                tableId={TableIds.users}
                columnNames={hideColumnWithoutGiveSpark(
                  this.state.columnNames,
                  "TY Segments",
                  this.props.usesGiveSpark
                )}
                columnData={this.columnData}
                extraColumns={this.extraColumns}
                selectionUpdate={this.selectionUpdate}
                checkCol
                selectAllInCheckboxHeader
                onSelectAllCheckboxChange={this.selectAllCheck}
                suppressedIds={this.state.suppressedIds}
                sortBy={this.state.sortBy.colName}
                selectAll={this.state.selectAll}
                sortAsc={this.state.sortAsc}
                columnHeaderSort={{
                  sortAccessor: this.state.sortBy.colName,
                  sortAscending: this.state.sortAsc,
                  sortableAccessors: sortOptions.map(o => o.colName),
                  onSort: this.handleColumnHeaderSort,
                }}
                searchText={this.state.searchTextUsed}
                filtered={!!this.state.searchTextUsed}
                footerMessage="Users Selected"
                noDataText={"No Users Found"}
                currentCharity={this.props.currentCharity}
                getResponse={this.getResponse}
              />
            )}
          </Row>
        </div>
      </main>
    );
  }
}

function mapState(state) {
  const charityId = state.user.currentCharity.id;
  return {
    thankYouAdminEmails: get(
      state.firebase,
      `data.${charityId}.internal.emails.thankYouAdminEmails`
    ),
    thankYouAdminSegments: get(
      state.firebase,
      `data.${charityId}.thankYouAdminSegments`
    ),
    segments: get(state.firebase, `data.${charityId}.segments`),
    usesGiveSpark: selectUsesGiveSpark(state),
  };
}

function firebasePaths(_, store) {
  const charityId = store.getState().user.currentCharity.id;
  return charityId
    ? [
        `${charityId}/internal/emails/thankYouAdminEmails`,
        `${charityId}/thankYouAdminSegments`,
        `${charityId}/segments`,
      ]
    : [];
}

const styledUsers = withStyles(s)(UsersPage);

export default serviceCaller(
  withLoginRedirect(
    withModals(connect(mapState)(firebaseConnect(firebasePaths)(styledUsers)))
  )
);
